import React, { useMemo, useState } from 'react';
import {
  Button,
  Form,
  Input,
  Modal,
  Popconfirm,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  Typography,
  message,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { DeleteOutlined, EditOutlined, PlusOutlined } from '@ant-design/icons';
import { type Barna } from '../api/barnaApi';
import { type Bhed } from '../api/bhedApi';
import {
  createPadachhedPratyaharLink,
  deletePadachhedPratyaharLink,
  updatePadachhedPratyaharLink,
  type AssociatedBarna,
  type PadachhedPratyaharLink,
} from '../api/ashtadhyayiPadachhedPratyaharApi';
import {
  associatedBarnaLabel,
  getPratyahar,
  type Pratyahar,
} from '../api/pratyaharApi';

type BarnaSelectionRow = {
  barnaId: number;
  include: boolean;
  included_bhedas: number[];
};

type LinkFormValues = {
  pratyaharId: number;
  barnaSelections: BarnaSelectionRow[];
};

function errMessage(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string | string[] } }; message?: string };
  const msg = ax?.response?.data?.message;
  if (Array.isArray(msg)) return msg.join('; ');
  if (typeof msg === 'string') return msg;
  return ax?.message || 'Request failed';
}

function selectionsToSelectedBarnas(
  rows: BarnaSelectionRow[],
  associatedBarnas: AssociatedBarna[]
): AssociatedBarna[] {
  return rows
    .map((r, index) => {
      if (!r.include) return null;
      const barnaId = r.barnaId ?? associatedBarnas[index]?.barnaId;
      if (barnaId == null || !Number.isFinite(Number(barnaId))) return null;
      return {
        barnaId: Number(barnaId),
        includedBhedas: (r.included_bhedas ?? []).map(Number).filter((n) => Number.isFinite(n)),
      };
    })
    .filter((x): x is AssociatedBarna => x != null);
}

function selectableBhedIds(
  assoc: AssociatedBarna | undefined,
  barnaId: number,
  barnaRows: Barna[],
  bhedRows: Bhed[]
): number[] {
  const barna = barnaRows.find((b) => b.id === barnaId);
  if (barna?.type === 'byanjan') return [];
  const fromAssoc = assoc?.includedBhedas ?? [];
  if (fromAssoc.length > 0) return [...fromAssoc];
  return bhedRows.filter((h) => h.barnaId === barnaId).map((h) => h.id);
}

function pratyaharToSelections(p: Pratyahar, barnaRows: Barna[], bhedRows: Bhed[]): BarnaSelectionRow[] {
  return p.associatedBarnas.map((a) => ({
    barnaId: a.barnaId,
    include: true,
    included_bhedas: selectableBhedIds(a, a.barnaId, barnaRows, bhedRows),
  }));
}

function linkToSelections(p: Pratyahar, link: PadachhedPratyaharLink): BarnaSelectionRow[] {
  const byBarna = new Map(link.selectedBarnas.map((s) => [s.barnaId, s]));
  return p.associatedBarnas.map((a) => {
    const sel = byBarna.get(a.barnaId);
    return {
      barnaId: a.barnaId,
      include: sel != null,
      included_bhedas: sel ? [...sel.includedBhedas] : [],
    };
  });
}

function selectedBarnasEqual(a: AssociatedBarna[], b: AssociatedBarna[]): boolean {
  const norm = (rows: AssociatedBarna[]) =>
    [...rows]
      .map((r) => ({
        barnaId: r.barnaId,
        includedBhedas: [...r.includedBhedas].sort((x, y) => x - y),
      }))
      .sort((x, y) => x.barnaId - y.barnaId);
  return JSON.stringify(norm(a)) === JSON.stringify(norm(b));
}

export type PadachhedLinkLookupData = {
  barnaRows: Barna[];
  bhedRows: Bhed[];
  lookupLoading: boolean;
};

type PadachhedPratyaharLinksPanelProps = PadachhedLinkLookupData & {
  padachhedId: number;
  padachhedText: string;
  links: PadachhedPratyaharLink[];
  pratyaharOptions: Pratyahar[];
  onChanged: () => Promise<void>;
};

export function PadachhedPratyaharLinksPanel({
  padachhedId,
  padachhedText,
  links,
  onChanged,
  barnaRows,
  bhedRows,
  lookupLoading,
  pratyaharOptions,
}: PadachhedPratyaharLinksPanelProps) {
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<PadachhedPratyaharLink | null>(null);
  const [saving, setSaving] = useState(false);
  const [activePratyahar, setActivePratyahar] = useState<Pratyahar | null>(null);
  const [form] = Form.useForm<LinkFormValues>();

  const barnaLabel = (id: number, assoc?: AssociatedBarna) =>
    associatedBarnaLabel(id, assoc, barnaRows);

  const bhedLabel = (id: number) => {
    const h = bhedRows.find((x) => x.id === id);
    return h ? `${h.text} (#${id})` : `#${id}`;
  };

  const pratyaharSelectOptions = useMemo(
    () =>
      pratyaharOptions.map((p) => ({
        value: p.id,
        label: `${p.name} (#${p.id})`,
        disabled: links.some((l) => l.pratyaharId === p.id && l.id !== editing?.id),
      })),
    [pratyaharOptions, links, editing?.id]
  );

  const loadPratyaharForForm = async (id: number): Promise<Pratyahar | null> => {
    const fromLink = links.find((l) => l.pratyaharId === id)?.pratyahar;
    if (fromLink && fromLink.associatedBarnas.length > 0) return fromLink;
    const cached = pratyaharOptions.find((p) => p.id === id);
    if (cached && cached.associatedBarnas.length > 0) return cached;
    try {
      return await getPratyahar(id);
    } catch (err: unknown) {
      message.error(errMessage(err));
      return null;
    }
  };

  const openCreate = () => {
    setEditing(null);
    setActivePratyahar(null);
    form.resetFields();
    form.setFieldsValue({ barnaSelections: [] });
    setModalOpen(true);
  };

  const openEdit = async (link: PadachhedPratyaharLink) => {
    const p = link.pratyahar ?? (await loadPratyaharForForm(link.pratyaharId));
    if (!p) return;
    setEditing(link);
    setActivePratyahar(p);
    form.setFieldsValue({
      pratyaharId: link.pratyaharId,
      barnaSelections: linkToSelections(p, link),
    });
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditing(null);
    setActivePratyahar(null);
    form.resetFields();
  };

  const onPratyaharChange = async (pratyaharId: number) => {
    const p = await loadPratyaharForForm(pratyaharId);
    if (!p) return;
    setActivePratyahar(p);
    form.setFieldsValue({ barnaSelections: pratyaharToSelections(p, barnaRows, bhedRows) });
  };

  const setBarnaInclude = (fieldIndex: number, barnaId: number, included: boolean) => {
    const assoc = activePratyahar?.associatedBarnas.find((a) => a.barnaId === barnaId);
    const allIds = selectableBhedIds(assoc, barnaId, barnaRows, bhedRows);
    form.setFields([
      { name: ['barnaSelections', fieldIndex, 'include'], value: included },
      { name: ['barnaSelections', fieldIndex, 'included_bhedas'], value: included ? allIds : [] },
    ]);
  };

  const onDelete = async (id: number) => {
    try {
      await deletePadachhedPratyaharLink(id);
      message.success('Link deleted');
      await onChanged();
    } catch (err: unknown) {
      message.error(errMessage(err));
    }
  };

  const submitLink = async () => {
    try {
      const values = await form.validateFields();
      if (!activePratyahar) {
        message.error('Select a pratyahar first');
        return;
      }
      const selectedBarnas = selectionsToSelectedBarnas(
        values.barnaSelections ?? [],
        activePratyahar.associatedBarnas
      );
      if (selectedBarnas.length === 0) {
        message.error('Select at least one barna for this link');
        return;
      }
      setSaving(true);
      if (editing) {
        if (selectedBarnasEqual(selectedBarnas, editing.selectedBarnas)) {
          message.info('No changes to save');
          closeModal();
          return;
        }
        await updatePadachhedPratyaharLink(editing.id, { selectedBarnas });
        message.success('Link updated');
      } else {
        await createPadachhedPratyaharLink({
          ashtadhyayiPadachhedId: padachhedId,
          pratyaharId: values.pratyaharId,
          selectedBarnas,
        });
        message.success('Link created');
      }
      closeModal();
      await onChanged();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const linkColumns: ColumnsType<PadachhedPratyaharLink> = [
    {
      title: 'Pratyahar',
      key: 'pratyahar',
      render: (_, link) => {
        const name = link.pratyahar?.name ?? pratyaharOptions.find((p) => p.id === link.pratyaharId)?.name;
        return name ? `${name} (#${link.pratyaharId})` : `#${link.pratyaharId}`;
      },
    },
    {
      title: 'Selected barnas',
      key: 'barnas',
      render: (_, link) =>
        link.selectedBarnas.length > 0 ? (
          <Space direction="vertical" size={4}>
            {link.selectedBarnas.map((a) => {
              const assoc = link.pratyahar?.associatedBarnas.find((x) => x.barnaId === a.barnaId);
              return (
              <div key={a.barnaId}>
                <Typography.Text>{barnaLabel(a.barnaId, assoc)}</Typography.Text>
                {a.includedBhedas.length > 0 ? (
                  <Space size={[4, 4]} wrap style={{ marginTop: 2 }}>
                    {a.includedBhedas.map((bid) => (
                      <Tag key={bid}>{bhedLabel(bid)}</Tag>
                    ))}
                  </Space>
                ) : (
                  <Typography.Text type="secondary" style={{ marginLeft: 8 }}>
                    (no bhedas)
                  </Typography.Text>
                )}
              </div>
            );
            })}
          </Space>
        ) : (
          <Typography.Text type="secondary">—</Typography.Text>
        ),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 120,
      render: (_, link) => (
        <Space size="small">
          <Button type="link" size="small" icon={<EditOutlined />} onClick={() => void openEdit(link)}>
            Edit
          </Button>
          <Popconfirm title="Delete this link?" onConfirm={() => void onDelete(link.id)}>
            <Button type="link" size="small" danger icon={<DeleteOutlined />}>
              Delete
            </Button>
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: '8px 0' }}>
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 8 }}>
        <Button
          type="dashed"
          size="small"
          icon={<PlusOutlined />}
          onClick={openCreate}
          disabled={lookupLoading}
        >
          Add link
        </Button>
      </Space>
      <Table<PadachhedPratyaharLink>
        rowKey="id"
        size="small"
        pagination={false}
        columns={linkColumns}
        dataSource={links}
        locale={{ emptyText: 'No pratyahar links' }}
      />
      <Modal
        title={editing ? `Edit link #${editing.id}` : 'Add pratyahar link'}
        open={modalOpen}
        onCancel={closeModal}
        onOk={() => void submitLink()}
        confirmLoading={saving}
        destroyOnClose
        width={560}
      >
        <Form form={form} layout="vertical" preserve={false}>
          <Form.Item
            name="pratyaharId"
            label="Pratyahar"
            rules={[{ required: true, message: 'Select a pratyahar' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              placeholder="Select pratyahar"
              options={pratyaharSelectOptions}
              disabled={editing != null}
              onChange={(id) => void onPratyaharChange(id)}
            />
          </Form.Item>
          {activePratyahar ? (
            <Form.List name="barnaSelections">
              {(fields) => (
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  {fields.length === 0 ? (
                    <Typography.Text type="secondary">
                      This pratyahar has no associated barnas.
                    </Typography.Text>
                  ) : null}
                  {fields.map((field) => {
                    const barnaId = form.getFieldValue(['barnaSelections', field.name, 'barnaId']) as number;
                    const barna = barnaRows.find((b) => b.id === barnaId);
                    const isByanjan = barna?.type === 'byanjan';
                    const assoc = activePratyahar.associatedBarnas.find((a) => a.barnaId === barnaId);
                    const selectableIds = selectableBhedIds(assoc, barnaId, barnaRows, bhedRows);
                    const bhedOptions = selectableIds
                      .map((bid) => {
                        const h = bhedRows.find((x) => x.id === bid);
                        return { value: bid, label: h ? `${h.text} (#${bid})` : `#${bid}` };
                      })
                      .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));
                    return (
                      <div
                        key={field.key}
                        style={{
                          border: '1px solid #f0f0f0',
                          borderRadius: 8,
                          padding: 12,
                        }}
                      >
                        <Form.Item name={[field.name, 'barnaId']} hidden>
                          <Input type="hidden" />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'include']}
                          label={barnaLabel(barnaId, assoc)}
                          valuePropName="checked"
                          style={{ marginBottom: 8 }}
                        >
                          <Switch
                            checkedChildren="Include"
                            unCheckedChildren="Exclude"
                            onChange={(checked) => setBarnaInclude(field.name, barnaId, checked)}
                          />
                        </Form.Item>
                        <Form.Item
                          name={[field.name, 'included_bhedas']}
                          label="Included bhedas"
                          normalize={(v) =>
                            Array.isArray(v) ? v.map((x) => Number(x)).filter((n) => Number.isFinite(n)) : []
                          }
                        >
                          <Select
                            mode="multiple"
                            options={bhedOptions}
                            placeholder={isByanjan ? 'Not applicable for byanjan' : 'Select bhedas'}
                            disabled={isByanjan}
                          />
                        </Form.Item>
                      </div>
                    );
                  })}
                </Space>
              )}
            </Form.List>
          ) : (
            <Typography.Text type="secondary">Select a pratyahar to choose barnas and bhedas.</Typography.Text>
          )}
        </Form>
      </Modal>
    </div>
  );
}
